import React, { useEffect, useState } from "react";
import {
  Flex,
  IconButton,
  HStack,
  Avatar,
  Box,
  Text,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
  MenuDivider,
  Image,
  Button,
  Badge,
  Tooltip,
  Grid,
  useMediaQuery,
  useToast,
  useDisclosure,
} from "@chakra-ui/react";
import { FiMenu, FiChevronDown } from "react-icons/fi";
import { SlBasket } from "react-icons/sl";
import { FaWallet } from "react-icons/fa";
import { Link, useNavigate } from "react-router-dom";
import { ChevronDownIcon } from "@chakra-ui/icons";
import SearchBar from "./SearchBar";
import { LinkItems } from "./NavItem";
import { useAuth } from "../../hooks/useAuth";
import { useCart } from "../../context/CartContext";
import { useWalletContext } from "../../context/WalletContext";
import NotificationDrawer from "./NotificationDrawer";
import WalletAccessButton from "../Wallet/WalletAccessButton";
import MerchantSwitchConfirmation from "../Dashboard/Merchant/MerchantSwitchConfirmation";

const MobileNav = ({ onOpen, ...rest }) => {
  let auth = useAuth();
  const { cartItems } = useCart();
  const { walletSummary } = useWalletContext();
  const [scrolling, setScrolling] = useState(false);
  const [switchingBack, setSwitchingBack] = useState(false);
  const { isOpen: isDialogOpen, onOpen: onDialogOpen, onClose: onDialogClose } = useDisclosure();
  const toast = useToast();
  const cancelRef = React.useRef();

  const cartValue = () => cartItems.reduce((count, item) => count + item.quantity, 0);

  const handleScroll = () => {
    setScrolling(window.scrollY > 0);
  };

  useEffect(() => {
    window.addEventListener("scroll", handleScroll);
    return () => {
      window.removeEventListener("scroll", handleScroll);
    };
  }, []);

  const navigate = useNavigate();
  const [isMobile] = useMediaQuery("(max-width: 768px)");


  const handleSwitchBackToMerchant = async () => {
    setSwitchingBack(true);
    try {
      await auth.switchBackToMerchant();
      toast({
        title: 'Switched back to Merchant',
        description: 'You are now viewing your merchant dashboard',
        status: 'success',
        duration: 3000,
        isClosable: true,
      });
      // Redirect to merchant dashboard
      navigate('/merchant/dashboard');
      onDialogClose();
    } catch (error) {
      console.error('Error switching back to merchant:', error);
      toast({
        title: 'Switch Failed',
        description: 'Failed to switch back to merchant view',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    } finally {
      setSwitchingBack(false);
    }
  };

  return (
    <>
      <Grid
        bg={scrolling ? "dark.500" : "transparent"}
        templateRows={{ base: "1fr 1fr", md: "1fr" }}
        templateColumns={{ base: "1fr 3fr 1fr", md: "1fr 6fr 1fr 1fr" }}
        px={{ base: "2rem", md: "2.5rem" }}
        py={2}
        gridColumnGap={4}
        gridRowGap={2}
        position="fixed"
        left={0}
        right={0}
        zIndex={4}
        h={{ base: "17vh", md: "15vh" }}
        alignItems="center"
        justifyContent={{ base: "center" }}
      >
        {/* Logo Section */}
        <Box
          alignItems="center"
          justifyContent="center"
          gridRowStart={{ base: 1, md: 1 }}
          gridRowEnd={{ base: 2, md: 2 }}
          gridColumnStart={{ base: 1, md: 1 }}
          gridColumnEnd={{ base: 2, md: 2 }}

        >
          <Link to="/">
            <Image
              src={`${process.env.PUBLIC_URL}/assets/logo_beatstake/beatstake.gif`}
              w={{ base: "5rem", md: "5rem" }}
              alt="Logo"
              cursor="pointer"
            />
          </Link>
        </Box>




        <Box display={{ base: 'none', md: 'block' }} gridRowStart={{ base: 1, md: 'auto' }}
          gridRowEnd={{ base: 2, md: "auto" }}
          gridColumnStart={{ base: 1, md: 2 }}
          gridColumnEnd={{ base: 2, md: 3 }} justifySelf={{ base: "flex-start", md: "flex-end", lg: "flex-start" }}>
          <Box display={{ md: 'block', lg: 'none' }}>
            <Menu>
              <MenuButton as={Button} rightIcon={<ChevronDownIcon />} variant="solid" _active={{ bg: "dark.500" }}>
                Menu
              </MenuButton>
              <MenuList>
                {LinkItems.map((link) => (
                  <MenuItem key={link.name} as={Link} to={link.link}>
                    {link.name}
                  </MenuItem>
                ))}
                {
                  auth.user && auth.user.account_type !== "fan" &&
                  <MenuItem key="subscription" as={Link} to="/subscriptions">
                    Subscriptions
                  </MenuItem>
                }
              </MenuList>
            </Menu>
          </Box>
          <HStack display={{ base: 'none', lg: 'flex' }} spacing={4}>
            {LinkItems.map((link) => (
              <Button key={link.name} as={Link} to={link.link} variant="ghost">
                {link.name}
              </Button>
            ))}
            {
              auth.user && auth.user.account_type !== "fan" &&
              <Button key="subscription" as={Link} to="/subscriptions" variant="ghost">
                Subscriptions
              </Button>
            }
          </HStack>
        </Box>


        {/* Cart and User Section */}
        <Box
          display={{ base: "flex" }}
          alignItems="center"
          justifyContent={{ base: "flex-end" }}
          gridRowStart={{ base: 1, md: 1 }}
          gridRowEnd={{ base: 2, md: 2 }}
          gridColumnStart={{ base: 2, md: 4 }}
          gridColumnEnd={{ base: 4, md: 5 }}

        >
          <Box display={{ base: "none", md: "flex" }} position="relative" mx={2}>
            <Badge
              position="absolute"
              color="dark.500"
              bg="dark.900"
              zIndex={5}
              left={7}
              top={-2}
            >
              {cartItems && cartItems.length !== 0 && cartValue()}
            </Badge>
            <Tooltip label="Basket" fontSize="md">
              <IconButton
                variant="ghost"
                as={Link}
                to="/cart"
                icon={<SlBasket />}
                aria-label="Basket"
              />
            </Tooltip>
          </Box>
          
          {/* Wallet Button - Only for authenticated users and desktop */}
          {auth.isAuthenticated && !isMobile && (
            <Box position="relative">
              <WalletAccessButton variant="default" size="md" />
            </Box>
          )}
          
          {auth.isAuthenticated && !isMobile && <NotificationDrawer />}

          {auth.isAuthenticated ? (
            <HStack spacing={{ base: "0", md: "6" }} mx={2}>
              <Flex alignItems={"center"}>
                <Menu>
                  <MenuButton py={2} transition="all 0.3s" _focus={{ boxShadow: "none" }}>
                    <HStack>
                      <Avatar
                        size="sm"
                        name={`${auth.user.firstname} ${auth.user.lastname}`}
                        bg="dark.900"
                        color="secondary.900"
                      />
                      <Box display={{ base: "none", md: "flex" }}>
                        <FiChevronDown />
                      </Box>
                    </HStack>
                  </MenuButton>
                  <MenuList>
                    <Text as="p" textAlign="left" p={2} color="dark.900">
                      Welcome {auth.user.username}!
                      {auth.isSwitchedContext && auth.user.currentContext && (
                        <Text fontSize="xs" color="primary.900" mt={1}>
                          Currently viewing: {auth.user.currentContext.artist_name || 'Artist'}
                        </Text>
                      )}
                    </Text>
                    <MenuDivider />

                    {/* Catalogue - show for artists and merchants in switched context */}
                    {
                      (auth.user.account_type === "artist" || auth.isSwitchedContext) && (
                        <>
                          <MenuItem as={Link} to={`/dashboard/artist/tracks`}>
                            Catalogue
                          </MenuItem>
                          <MenuItem as={Link} to={`/dashboard/artist/featured-tracks`}>
                            Featured Tracks
                          </MenuItem>
                        </>
                      )
                    }

                    {/* Dashboard or Switch back to Merchant */}
                    {
                      auth.user.account_type === "merchant" && !auth.isSwitchedContext && (
                        <MenuItem as={Link} to={`/merchant/dashboard`}>
                          Dashboard
                        </MenuItem>
                      )
                    }

                    {/* Switch back to Merchant button - only for merchants who are switched to artist context */}
                    {auth.user.account_type === "merchant" && auth.isSwitchedContext && (
                      <>
                        <MenuItem
                          onClick={onDialogOpen}
                          isDisabled={switchingBack}
                          bg="dark.500"
                          color="primary.100"
                          _hover={{ bg: "secondary.900" }}
                          fontWeight="bold"
                        >
                          Switch to Merchant
                        </MenuItem>
                        <MenuDivider />
                      </>
                    )}

                    {/* Hide Portfolio for linked artists (when in switched context) */}
                    {!auth.isSwitchedContext && (
                      <MenuItem as={Link} to={`/dashboard/collections`}>
                        Portfolio
                      </MenuItem>
                    )}
                    <MenuItem as={Link} to={`/dashboard/${auth.isSwitchedContext ? 'artist' : auth.user.account_type}/profile`}>
                      Settings
                    </MenuItem>
                    <MenuItem as={Link} to={`/dashboard/billing`}>
                      Transactions
                    </MenuItem>
                    <MenuItem as={Link} to="/referral">
                      Referral
                    </MenuItem>
                    <MenuDivider />
                    <MenuItem
                      onClick={() => {
                        auth.logout();
                        toast({
                          title: "Logged out",
                          description: "You have been logged out successfully",
                          status: "success",
                          duration: 9000,
                          isClosable: true,
                        });
                        navigate("/");
                      }}
                    >
                      Log out
                    </MenuItem>
                  </MenuList>
                </Menu>
              </Flex>
            </HStack>
          ) : (
            <Button variant="ghost" as={Link} to="/login" color="dark.900">
              Login
            </Button>
          )}

        </Box>

        {/* Search Bar Section */}
        <Box
          gridRowStart={{ base: 2, md: 1 }}
          gridRowEnd={{ base: 3, md: 2 }}
          gridColumnStart={{ base: 1, md: 3 }}
          gridColumnEnd={{ base: 4, md: 4 }}
          w={{ base: "100%", md: "50%", lg: "100%" }}
          display="flex"
          justifySelf={{ base: "center", md: "flex-end" }}

        >
          <SearchBar />
        </Box>
      </Grid>

      {/* Mobile Bottom Navigation Bar */}
      {isMobile && (
        <Flex
          position="fixed"
          bottom={0}
          left={0}
          width="100%"
          bg="secondary.100"
          p={3}
          justifyContent="space-around"
          alignItems="center"
          zIndex={10}
          borderTop="1px solid"
          borderColor="dark.200"
        >
          <IconButton
            icon={<FiMenu />}
            aria-label="Menu"
            variant="ghost"
            onClick={onOpen}
            size="lg"
          />
          
          <Box position="relative">
            <Badge
              position="absolute"
              color="dark.500"
              bg="dark.900"
              zIndex={5}
              left={7}
              top={-2}
              borderRadius="full"
              fontSize="xs"
            >
              {cartItems && cartItems.length !== 0 && cartValue()}
            </Badge>
            <Tooltip label="Basket" fontSize="md">
              <IconButton
                variant="ghost"
                as={Link}
                to="/cart"
                icon={<SlBasket />}
                aria-label="Basket"
                size="lg"
              />
            </Tooltip>
          </Box>

          {/* Wallet Button - Only for authenticated users */}
          {auth.isAuthenticated && (
            <Box position="relative">
              <Badge
                position="absolute"
                color="green.500"
                bg="secondary.500"
                zIndex={5}
                left={7}
                top={-2}
              >
                ${walletSummary?.available_balance?.toFixed(2) || '0.00'}
              </Badge>
              <Tooltip 
                label="Wallet" 
                fontSize="sm"
                placement="top"
              >
                <IconButton
                  variant="ghost"
                  as={Link}
                  to="/wallet"
                  icon={<FaWallet />}
                  aria-label="Wallet"
                  size="lg"
                  color="dark.900"
                  _hover={{
                    color: "dark.700",
                    transform: "scale(1.05)"
                  }}
                  transition="all 0.2s"
                />
              </Tooltip>
            </Box>
          )}

          {auth.isAuthenticated && <NotificationDrawer />}
        </Flex>
      )}

      {/* Switch Back Confirmation Dialog */}
      <MerchantSwitchConfirmation
        isOpen={isDialogOpen}
        onClose={onDialogClose}
        merchantName={auth.user?.company_name}
        onConfirm={handleSwitchBackToMerchant}
        isLoading={switchingBack}
        cancelRef={cancelRef}
      />
    </>
  );
};

export default MobileNav;